import type {
  Comment,
  DeleteCommentResult,
  Thread,
  UpdateThreadPayload,
} from '@atlas/api-contract';
import { CheckCircle2, Loader2, RotateCcw, X } from 'lucide-react';
import { useState } from 'react';

import { toDisplayableError } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { formatAbsoluteTime, formatRelativeTime } from '@/lib/format/date';

import { CommentComposer } from './comment-composer';
import { CommentItem } from './comment-item';
import { DeleteCommentDialog } from './delete-comment-dialog';
import { threadFirstComment } from './thread-display';
import { toCreateCommentPayload } from './thread-schemas';
import { useCreateComment, useSetThreadResolved } from './threads-queries';

export interface ThreadPopoverProps {
  readonly thread: Thread;
  readonly versionId: string;
  /**
   * The agent could not re-find the anchored element and placed the pin from
   * the stored page fraction instead (spec R4.6).
   */
  readonly approximate: boolean;
  readonly onClose: () => void;
  /** Called after a delete, with the server's answer, so the pin can be dropped. */
  readonly onCommentDeleted: (result: DeleteCommentResult) => void;
}

/**
 * One thread's conversation, floated beside its pin (spec R5.2).
 *
 * ============================================================================
 * FLAT, ON PURPOSE
 * ============================================================================
 * The opening comment then its replies in one list, with no nesting and no
 * reply-to-reply. Threading a design review produces a tree nobody reads: the
 * subject is the pin, and everything said about it is said about the same
 * thing. The opening comment is separated from the replies by a rule rather
 * than by indentation, which keeps that hierarchy — one subject, N answers —
 * legible without pretending there is a second level.
 *
 * ============================================================================
 * AN APPROXIMATE PIN SAYS SO
 * ============================================================================
 * When the stored selector matches nothing — the prototype changed, the element
 * is inside a closed accordion, the selector now matches two things — the agent
 * falls back to the page coordinates the click was recorded at and marks the
 * pin approximate. The popover repeats that in words, in the
 * `pin-approximate` colour, because a pin sitting confidently 200 pixels from
 * what somebody was talking about is worse than a pin that admits it is lost.
 */
export const ThreadPopover = ({
  thread,
  versionId,
  approximate,
  onClose,
  onCommentDeleted,
}: ThreadPopoverProps) => {
  const createComment = useCreateComment();
  const setResolved = useSetThreadResolved();

  const [pendingDelete, setPendingDelete] = useState<Comment | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);

  const first = threadFirstComment(thread);
  const isResolved = thread.resolvedAt !== null;

  const handleReply = async (body: string): Promise<boolean> => {
    /*
     * Authorship comes from the session now (Phase 2): any signed-in user may
     * reply, and the server records who wrote it.
     */
    await createComment.mutateAsync({
      versionId,
      threadId: thread.id,
      payload: toCreateCommentPayload(body),
    });

    return true;
  };

  const applyResolved = (payload: UpdateThreadPayload): void => {
    setResolveError(null);

    setResolved.mutate(
      { versionId, threadId: thread.id, payload },
      {
        onError: (error) => {
          setResolveError(toDisplayableError(error));
        },
      },
    );
  };

  const handleToggleResolved = (): void => {
    /*
     * Resolve and reopen both derive the actor from the session now (Phase 2):
     * the payload is just the new state, and the server records `resolvedBy`.
     * Any signed-in user may resolve or reopen a thread (spec matrix).
     */
    applyResolved({ resolved: !isResolved });
  };

  return (
    <Card className="flex max-h-[min(28rem,70vh)] w-80 flex-col overflow-hidden shadow-lg">
      <header className="flex shrink-0 items-start gap-2 px-3 pt-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-medium text-foreground">
            {thread.createdByName}
          </p>
          <time
            className="text-xs text-muted-foreground"
            dateTime={thread.createdAt}
            title={formatAbsoluteTime(thread.createdAt)}
          >
            {formatRelativeTime(thread.createdAt)}
          </time>
        </div>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant={isResolved ? 'secondary' : 'ghost'}
              size="icon"
              className="size-7 shrink-0"
              disabled={setResolved.isPending}
              aria-pressed={isResolved}
              onClick={handleToggleResolved}
            >
              {setResolved.isPending ? (
                <Loader2 aria-hidden="true" className="size-4 animate-spin" />
              ) : isResolved ? (
                <RotateCcw aria-hidden="true" className="size-4" />
              ) : (
                <CheckCircle2 aria-hidden="true" className="size-4" />
              )}
              <span className="sr-only">
                {isResolved ? 'Reopen this thread' : 'Resolve this thread'}
              </span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            {isResolved ? 'Reopen this thread' : 'Mark this thread resolved'}
          </TooltipContent>
        </Tooltip>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7 shrink-0 text-muted-foreground hover:text-foreground"
          onClick={onClose}
        >
          <X aria-hidden="true" className="size-4" />
          <span className="sr-only">Close this thread</span>
        </Button>
      </header>

      {isResolved ? (
        <div className="shrink-0 px-3 pt-2">
          <Badge
            variant="secondary"
            className="gap-1 bg-pin-resolved/15 font-normal text-foreground"
          >
            <CheckCircle2 aria-hidden="true" className="size-3" />
            Resolved
            {thread.resolvedByName === null
              ? ''
              : ` by ${thread.resolvedByName}`}
          </Badge>
        </div>
      ) : null}

      {approximate ? (
        <p className="mx-3 mt-2 shrink-0 rounded-md border border-pin-approximate/40 bg-pin-approximate/10 px-2.5 py-1.5 text-xs text-foreground">
          The original element wasn&rsquo;t found on this screen, so this pin is
          placed approximately from where the comment was left.
        </p>
      ) : null}

      {resolveError === null ? null : (
        <p
          role="alert"
          className="mx-3 mt-2 shrink-0 rounded-md border border-destructive/30 bg-destructive/5 px-2.5 py-1.5 text-xs text-destructive"
        >
          {resolveError}
        </p>
      )}

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-3 py-3">
        {first === null ? (
          <p className="text-sm italic text-muted-foreground">
            This thread has no comments left on it.
          </p>
        ) : (
          <>
            <CommentItem
              comment={first}
              versionId={versionId}
              isFirst
              onRequestDelete={setPendingDelete}
            />

            {thread.comments.length > 1 ? (
              <Separator className="!my-3" />
            ) : null}

            {thread.comments.slice(1).map((reply) => (
              <CommentItem
                key={reply.id}
                comment={reply}
                versionId={versionId}
                isFirst={false}
                onRequestDelete={setPendingDelete}
              />
            ))}
          </>
        )}
      </div>

      <div className="shrink-0 border-t bg-muted/30 px-3 py-2.5">
        <CommentComposer
          label={`Reply to ${thread.createdByName}`}
          placeholder="Reply…"
          submitLabel="Reply"
          pendingLabel="Posting…"
          rows={2}
          onSubmit={handleReply}
        />
      </div>

      <DeleteCommentDialog
        comment={pendingDelete}
        thread={thread}
        versionId={versionId}
        onOpenChange={(open) => {
          if (!open) {
            setPendingDelete(null);
          }
        }}
        onDeleted={onCommentDeleted}
      />
    </Card>
  );
};
